import { useEffect } from "react";
import styled from "@emotion/styled";
import { Global } from "@emotion/react";
import { Nav } from "../navigationAndRouting/Nav";
import { Page } from "../navigationAndRouting/Page";
import { useLocation } from "react-router-dom";
import { useSingleton } from "../injection";
import { RouteStore } from "../navigationAndRouting/RouteStore";
import { observer } from "mobx-react-lite";
import { AngularLoaderStore } from "../bridgeSetup/AngularLoaderStore";
import { windowAsAny } from "../windowAsAny/windowAsAny";
import { UnsavedChangesProvider } from "../navigationAndRouting/useUnsavedChanges";
import { brandName } from "../../branding/branding";

export const AppSkeleton = observer(() => {
    const angularLoaderStore = useSingleton(AngularLoaderStore);

    const location = useLocation();
    const routeStore = useSingleton(RouteStore);
    const currentRoute = routeStore.getRouteByPath(location.pathname);

    const showNav = !currentRoute?.isPrintableView;

    // title change
    useEffect(() => {
        // update document title on page change
        if (currentRoute?.title !== undefined) {
            document.title = `${brandName}: ${
                routeStore.getRouteTitle(currentRoute) ?? ""
            }`;
        }
    }, [currentRoute, routeStore]);

    // send legacy event to angular root scope (this will trigger PopupService to close popups on page change)
    useEffect(() => {
        if (angularLoaderStore.hasLoaded) {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-call
            windowAsAny()
                // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                .RedDeer.UI.App.getInject("$rootScope")
                // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                .$broadcast("PopupService_closePopups");
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentRoute]);

    return (
        <Container>
            {showNav && <Nav />}
            {currentRoute?.isPrintableView && (
                <Global styles={{ html: { overflow: "visible" } }} />
            )}
            <Content
                data-testid="appContent"
                isPrint={currentRoute?.isPrintableView ?? false}
            >
                <UnsavedChangesProvider>
                    <Page />
                </UnsavedChangesProvider>
            </Content>
        </Container>
    );
});

const Container = styled("div")({
    zIndex: 1, // using z-index here means that child elements with z-index values will not affect top level elements with z-indexes such as popups
    position: "absolute",
    height: "100%",
    width: "100%",
    display: "flex",
    flexDirection: "row"
});

const Content = styled("div")<{ isPrint: boolean }>(props => ({
    flexGrow: 1,
    position: "relative",
    overflow: props.isPrint ? undefined : "hidden"
}));
